/**
 * Stocktake list widgets (#1805 slice A1).
 *
 * These five projections used to be `fields[].render` callbacks inside
 * `stocktakeResource.tsx`. The v2 author face has no carrier for
 * `render` — deliberately, since manifest §2 lands it on `Widget` — so moving
 * the resource onto it means naming them.
 *
 * Two things about them are load-bearing and must not be "tidied":
 *
 *   - Each declares `csvText: stringifyRaw(value)`. That is not a design
 *     choice, it is the CSV the legacy `render` carrier already produced:
 *     `formatFieldTextForExport` degrades a bespoke renderer to the raw value
 *     because arbitrary JSX has no text projection. Omitting `csvText` would
 *     silently move every one of these columns to its field TYPE's projection
 *     (`'' → -`), which `src/lib/__tests__/csv-export-lock.test.ts` catches.
 *   - The markup is copied verbatim, class list included. The parity lock in
 *     `__tests__/stocktakeResource.v2-parity.test.tsx` compares rendered HTML
 *     byte for byte on both the list and the detail surface.
 */
import { useTranslation } from 'react-i18next';
import { failHard } from '@resource-kit/core';
import { stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import type { Stocktake, StocktakeStatus } from '@/services';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    stocktakeWeek: { types: 'text'; modes: 'list' };
    stocktakeStatus: { types: 'text'; modes: 'list' };
    stocktakeOpenedAt: { types: 'text'; modes: 'list' };
    stocktakeClosedAt: { types: 'text'; modes: 'list' };
    stocktakeOpenedBy: { types: 'text'; modes: 'list' };
  }
}

const EM_DASH = '—';

/**
 * The stocktake behind the cell.
 *
 * A row-aware projection mounted without its record is an authoring mistake,
 * not a value to render around: the previous `render` callbacks read
 * `row.id` / `row.status` unguarded and would have thrown on the same input.
 * Failing here says which widget and why.
 */
function stocktakeOf(record: unknown, widget: string): Stocktake {
  if (typeof record !== 'object' || record === null) {
    return failHard(`${widget}: mounted without a stocktake record`);
  }
  return record as Stocktake;
}

/** The legacy `render` carrier's CSV projection, kept exactly (see the header). */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

function StocktakeWeek({ value, record }: WidgetProps) {
  const row = stocktakeOf(record, 'stocktakeWeek');
  return (
    <span className="font-medium tabular-nums" data-testid={`stocktake-row-${row.id}`}>
      {String(value ?? '')}
    </span>
  );
}

/** Tailwind status badge classes per stocktake status — the bespoke colour-coding. */
function statusClass(status: StocktakeStatus): string {
  switch (status) {
    case 'open':
      return 'bg-primary/10 text-primary';
    case 'counted':
      return 'bg-warning/10 text-foreground';
    case 'committed':
      return 'bg-success/10 text-foreground';
    case 'cancelled':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * The badge reads the ROW, not the cell value, exactly as the `render` callback
 * did — and routes the label through the `stocktake.status.*` keys so a raw
 * enum never reaches the operator.
 */
function StocktakeStatusBadge({ record }: WidgetProps) {
  const { t } = useTranslation();
  const row = stocktakeOf(record, 'stocktakeStatus');
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${statusClass(row.status)}`}>
      {t(`stocktake.status.${row.status}`)}
    </span>
  );
}

function StocktakeOpenedAt({ value }: WidgetProps) {
  return <span className="text-muted-foreground">{String(value ?? '').slice(0, 10)}</span>;
}

/** The one asymmetry with `openedAt`: a stocktake that is still open has no close date. */
function StocktakeClosedAt({ value }: WidgetProps) {
  return (
    <span className="text-muted-foreground">{value ? String(value).slice(0, 10) : EM_DASH}</span>
  );
}

function StocktakeOpenedBy({ value }: WidgetProps) {
  return <span className="text-muted-foreground">{String(value ?? '')}</span>;
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerStocktakeWidgets(): void {
  registerWidget('stocktakeWeek', {
    display: StocktakeWeek,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stocktakeStatus', {
    display: StocktakeStatusBadge,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stocktakeOpenedAt', {
    display: StocktakeOpenedAt,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stocktakeClosedAt', {
    display: StocktakeClosedAt,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stocktakeOpenedBy', {
    display: StocktakeOpenedBy,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
